import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Card, H2, Link, Separator, Text } from '@betterment/samba-react';
import { CardLoader, useFeatureFlag } from '@betterment/shared-components';

import { EnvelopeRow } from './components/EnvelopeRow';
import { EnvelopesReorderDialog } from './EnvelopesReorderDialog';
import { EnvelopesCardDocument } from './graphql/query.envelopesCard.generated';

import { EnvelopeAccountPerformanceHistoryDocument } from '../../account/components/overview-card/graphql/envelopeAccountPerformanceHistory.generated';
import { DateRangeOption } from '../../graphql/schema';
import { IdempotencyProviderWithFeatureRegistry } from '../../RetailApp/graphql/idempotency/IdempotencyProviderWithFeatureRegistry';
import { Prefetch } from '../../Shared/components-v2/prefetch/Prefetch';

export function EnvelopesCard() {
  const { enabled: isEnvelopesReorderingEnabled } = useFeatureFlag({
    splitName: 'retail.envelopes_reordering_q4_2025_enabled',
    context: 'EnvelopesCard',
  });

  const { data } = useGraphQLQuery(EnvelopesCardDocument, { throwOnError: true });
  if (!data) return <CardLoader />;

  const { envelopes } = data;
  const sortedEnvelopes = [...envelopes];

  if (!envelopes.length) return null;

  if (isEnvelopesReorderingEnabled) {
    sortedEnvelopes.sort((a, b) => {
      const isAChecking = a.accounts.some(account => account.__typename === 'CheckingAccount');
      const isBChecking = b.accounts.some(account => account.__typename === 'CheckingAccount');

      // Keep checking accounts first
      if (isAChecking && !isBChecking) return -1;
      if (!isAChecking && isBChecking) return 1;

      // If both have checking or neither have checking, sort by orderPosition
      if (a.orderPosition == null && b.orderPosition == null) return 0;
      if (a.orderPosition == null) return 1;
      if (b.orderPosition == null) return -1;
      return a.orderPosition - b.orderPosition;
    });
  }

  const accountsWithoutPurpose = envelopes.flatMap(e =>
    e.purpose ? [] : e.accounts.filter(a => ['ManagedAccount', 'DirectTradingAccount'].includes(a.__typename))
  );
  const queries = accountsWithoutPurpose.map(account => ({
    query: EnvelopeAccountPerformanceHistoryDocument,
    variables: { id: account.id, dateRangeOption: DateRangeOption.AllTime },
    uniqueKey: `EnvelopeAccountPerformanceHistory-${account.id}`,
  }));

  return (
    <Prefetch queries={queries}>
      <Card data-testid="specPageAccountCards">
        <Card.Header css={{ pt: 'space.300', px: 'space.300' }}>
          {isEnvelopesReorderingEnabled ? (
            <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
              <H2 variant="title200">Accounts</H2>
              <IdempotencyProviderWithFeatureRegistry featureName="envelopes">
                <EnvelopesReorderDialog envelopes={sortedEnvelopes} />
              </IdempotencyProviderWithFeatureRegistry>
            </Box>
          ) : (
            <H2 variant="title200">Accounts</H2>
          )}
          <Separator css={{ mt: 'space.300' }} />
        </Card.Header>
        <Card.Content>
          {sortedEnvelopes.map(envelope => (
            <EnvelopeRow key={envelope.id} envelope={envelope} />
          ))}
        </Card.Content>
        <Card.Footer css={{ p: 'space.300' }}>
          <Link href="#disclosures" css={{ _hover: { color: 'text.weak' } }} data-turbolinks="false">
            <Text variant="text50" color="text.weak">
              See disclosures below
            </Text>
          </Link>
        </Card.Footer>
      </Card>
    </Prefetch>
  );
}
